import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OidcConfigurationNameContext } from "../contexts/OidcConfigurationNameContext";
import { useOidcUser, useOidcIdToken, useOidc } from "@axa-fr/react-oidc";

const AuthorizationErrorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const { logout } = useOidc(oidcConfigurationName);
  const { idToken } = useOidcIdToken(oidcConfigurationName);
  const [userInfo, setUserInfo] = useState<any>(null);

  // Get error details from URL params
  const errorMessage = searchParams.get('error') || 'Access denied';
  const endpoint = searchParams.get('endpoint') || 'Unknown endpoint';

  useEffect(() => {
    // Try to extract user info from the token for troubleshooting
    if (idToken) {
      setUserInfo(idToken);
    }
  }, [idToken]);

  const handleLoginRedirect = () => {
    // Clear any cached tokens and redirect to login
    logout();
    navigate('/login');
  };

  const copyTroubleshootingInfo = () => {
    const troubleshootingData = {
      timestamp: new Date().toISOString(),
      endpoint: endpoint,
      error: errorMessage,
      oidcProvider: oidcConfigurationName,
      userEmail: userInfo?.email || 'Unknown',
      userGroups: userInfo?.groups || [],
      userDomain: userInfo?.hd || userInfo?.email?.split('@')[1] || 'Unknown',
      userId: userInfo?.sub || 'Unknown',
      tokenAudience: userInfo?.aud || 'Unknown'
    };

    navigator.clipboard.writeText(JSON.stringify(troubleshootingData, null, 2));
    alert('Troubleshooting information copied to clipboard');
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '2rem',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '3rem',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '700px',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            color: '#dc3545'
          }}>
            🚫
          </div>
          <h1 style={{
            color: '#dc3545',
            marginBottom: '0.5rem',
            fontSize: '1.8rem'
          }}>
            Access Denied (401 Unauthorized)
          </h1>
          <p style={{
            color: '#6c757d',
            fontSize: '1rem',
            margin: 0
          }}>
            You are not authorized to use this application
          </p>
        </div>

        {/* User Information */}
        {userInfo && (
          <div style={{
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #dee2e6'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.1rem' }}>
              Current User Information:
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              <p><strong>Email:</strong> {userInfo.email || 'Not available'}</p>
              <p><strong>Name:</strong> {userInfo.name || userInfo.preferred_username || 'Not available'}</p>
              <p><strong>Domain:</strong> {userInfo.hd || userInfo.email?.split('@')[1] || 'Not available'}</p>
              <p><strong>Provider:</strong> {oidcConfigurationName}</p>
            </div>
          </div>
        )}

        {/* Error Details */}
        <div style={{
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#fff3cd',
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#856404', fontSize: '1.1rem' }}>
            Error Details:
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#856404' }}>
            <p><strong>Endpoint:</strong> {endpoint}</p>
            <p><strong>Error:</strong> {errorMessage}</p>
            <p><strong>Time:</strong> {new Date().toLocaleString()}</p>
          </div>
        </div>

        {/* Troubleshooting Information */}
        <div style={{
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          border: '1px solid #b3d9ff'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#004085', fontSize: '1.1rem' }}>
            Possible Causes:
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '1.5rem',
            color: '#004085',
            fontSize: '0.9rem',
            lineHeight: '1.5'
          }}>
            <li>Your account is not a member of the required security groups</li>
            <li>Your account domain is not authorized for this application</li>
            <li>Your group membership may have changed recently</li>
            <li>The application configuration may have been updated</li>
            <li>Your authentication token may have expired or be invalid</li>
          </ul>
        </div>

        {/* Technical Information for Admins */}
        <div style={{
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.1rem' }}>
            For Technical Support:
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '1rem' }}>
            If you need to contact your administrator, please provide the following information:
          </p>
          <button
            onClick={copyTroubleshootingInfo}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '1rem'
            }}
          >
            📋 Copy Troubleshooting Info
          </button>
          <div style={{
            fontSize: '0.8rem',
            color: '#6c757d',
            fontFamily: 'monospace',
            backgroundColor: '#f1f3f4',
            padding: '0.5rem',
            borderRadius: '4px',
            maxHeight: '150px',
            overflow: 'auto'
          }}>
            User ID: {userInfo?.sub || 'Unknown'}<br/>
            Email: {userInfo?.email || 'Unknown'}<br/>
            Domain: {userInfo?.hd || userInfo?.email?.split('@')[1] || 'Unknown'}<br/>
            Provider: {oidcConfigurationName}<br/>
            Endpoint: {endpoint}<br/>
            Error: {errorMessage}
          </div>
        </div>

        {/* Action Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={handleLoginRedirect}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Login with Different Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationErrorPage; 